import React, { useState } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { AdminStoreInvoiceResource, MarkStoreInvoicePaidRequest } from '../types';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';

interface StoreInvoiceDetailSlideOverProps {
  invoice: AdminStoreInvoiceResource | null;
  isOpen: boolean;
  onClose: () => void;
  onPaidSuccess: () => void;
}

const statusBadge = (status: string) => {
  const styles: Record<string, string> = {
    paid: 'bg-green-100 text-green-800',
    unpaid: 'bg-yellow-100 text-yellow-800',
    expired: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${styles[status] ?? 'bg-gray-100 text-gray-800'}`}>
      {status.toUpperCase()}
    </span>
  );
};

const formatRupiah = (amount: number) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(amount);

const StoreInvoiceDetailSlideOver: React.FC<StoreInvoiceDetailSlideOverProps> = ({
  invoice,
  isOpen,
  onClose,
  onPaidSuccess,
}) => {
  const { canManage } = useAuth();
  const [proofUrl, setProofUrl] = useState('');
  const [proofNotes, setProofNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMarkPaid = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoice) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: MarkStoreInvoicePaidRequest = {
        payment_proof_url: proofUrl,
        payment_proof_notes: proofNotes || undefined,
      };
      await api.markStoreInvoicePaidManual(invoice.id, payload);
      setProofUrl('');
      setProofNotes('');
      onPaidSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'An error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !invoice) return null;

  return (
    <div className="fixed inset-0 overflow-hidden z-50">
      <div className="absolute inset-0 overflow-hidden">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />

        {/* Slide-over panel */}
        <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
          <div className="w-screen max-w-md">
            <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
              {/* Header */}
              <div className="px-4 py-6 bg-gray-50 sm:px-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Invoice Detail</h2>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="mt-1 text-sm text-gray-500">{invoice.invoiceNumber}</p>
              </div>

              <div className="flex-1 px-4 py-6 sm:px-6 space-y-6">
                {/* Status */}
                <div className="flex items-center gap-3">
                  {statusBadge(invoice.status)}
                  {invoice.paidAt && (
                    <span className="text-sm text-gray-500">
                      Paid at {new Date(invoice.paidAt).toLocaleDateString('id-ID')}
                    </span>
                  )}
                </div>

                {/* Company + Store Item */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Company</p>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5">{invoice.company.name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Feature</p>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5">{invoice.storeItem.name}</p>
                    <p className="text-xs text-gray-400">{invoice.storeItem.key}</p>
                  </div>
                </div>

                {/* Price Breakdown */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Price Breakdown</p>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Price</span>
                      <span>{formatRupiah(invoice.price)}</span>
                    </div>
                    {invoice.months && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Duration</span>
                        <span>{invoice.months} bulan</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total</span>
                      <span>{formatRupiah(invoice.total)}</span>
                    </div>
                    {invoice.discount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount</span>
                        <span>- {formatRupiah(invoice.discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold border-t pt-1.5 border-gray-200">
                      <span>Grand Total</span>
                      <span>{formatRupiah(invoice.grandTotal)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment proof (if already paid manually) */}
                {invoice.status === 'paid' && invoice.paymentGateway === 'manual' && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Payment Evidence</p>
                    {/* Invoice dari Pembelian Manual menyimpan path storage private (bukan URL); buktinya dilihat di menu Pembelian Manual. */}
                    {invoice.paymentProofUrl && /^https?:\/\//i.test(invoice.paymentProofUrl) && (
                      <a
                        href={invoice.paymentProofUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-primary-600 hover:underline"
                      >
                        <ExternalLink className="h-4 w-4" />
                        View Proof
                      </a>
                    )}
                    {invoice.paymentProofNotes && (
                      <p className="mt-1 text-sm text-gray-600 bg-gray-50 rounded p-2">
                        {invoice.paymentProofNotes}
                      </p>
                    )}
                  </div>
                )}

                {/* Mark as Paid form — only for unpaid */}
                {canManage && invoice.status === 'unpaid' && (
                  <div className="border-t pt-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4">Mark as Paid Manually</h3>

                    {error && (
                      <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{error}</div>
                    )}

                    <form onSubmit={handleMarkPaid} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Payment Proof URL <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={proofUrl}
                          onChange={e => setProofUrl(e.target.value)}
                          placeholder="https://drive.google.com/..."
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <p className="mt-1 text-xs text-gray-400">Paste a URL to the payment screenshot or receipt.</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Notes</label>
                        <textarea
                          value={proofNotes}
                          onChange={e => setProofNotes(e.target.value)}
                          rows={3}
                          placeholder="Optional admin notes..."
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {submitting && <LoadingSpinner size="sm" />}
                        Confirm Payment
                      </button>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreInvoiceDetailSlideOver;
